import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router';
import { deleteReel, getReels, isTwitchSignInRequiredError, renameReel } from '../api/rttsClient';
import type { ReelSummary } from '../api/types';
import { setMockAuthState } from '../api/mockState';
import ReelCard from '../components/ReelCard';
import ReconnectTwitchCard from '../components/ReconnectTwitchCard';
import StudioToast from '../components/StudioToast';
import {
  REELS_LIBRARY_POLL_MS,
  STUDIO_COPY,
  STUDIO_DOCUMENT_TITLES,
  isStudioJobInProgress,
  isStudioUseMock,
} from '../config';
import { beginTwitchConnect } from '../auth/beginTwitchConnect';
import { useStudioAuth } from '../hooks/useStudioAuth';
import { useStudioDocumentTitle } from '../hooks/useStudioDocumentTitle';
import { useReelsLibraryRevalidate } from '../hooks/useReelsLibraryRevalidate';
import { useStudioReelsBadge } from '../hooks/useStudioReelsBadge';
import { reelDisplayTitle } from '../utils/reelName';
import {
  reelFilterCounts,
  reelMatchesFilter,
  type ReelFilter,
} from '../utils/studioHome';

/**
 * My Reels library — newest-first cards with live status while cutting.
 * Polls while any reel is in-progress; toasts when one becomes delivered.
 * Refetches on tab focus / visibility so a cross-device delete is not a stale snapshot.
 */
const StudioReelsPage: React.FC = () => {
  useStudioDocumentTitle(STUDIO_DOCUMENT_TITLES.reels);
  const { status, isLoggedOut, setMockState, refreshMock } = useStudioAuth();
  const { reportReels } = useStudioReelsBadge();
  const [reels, setReels] = useState<ReelSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reconnect, setReconnect] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [filter, setFilter] = useState<ReelFilter>('all');
  const prevStatusRef = useRef<Map<string, string>>(new Map());
  const seededRef = useRef(false);

  const applyReels = useCallback(
    (next: ReelSummary[], { announce }: { announce: boolean }) => {
      if (announce && seededRef.current) {
        for (const reel of next) {
          const prev = prevStatusRef.current.get(reel.job_id);
          if (
            prev &&
            isStudioJobInProgress(prev) &&
            reel.status === 'delivered'
          ) {
            const title = reelDisplayTitle(reel);
            setToast(`${STUDIO_COPY.reelsReadyToastPrefix} — ${title}`);
          }
        }
      }
      const map = new Map<string, string>();
      for (const reel of next) {
        map.set(reel.job_id, reel.status);
      }
      prevStatusRef.current = map;
      seededRef.current = true;
      setReels(next);
      reportReels(next);
    },
    [reportReels]
  );

  const loadInFlightRef = useRef(false);
  const load = useCallback(
    async (opts: { announce: boolean }) => {
      if (loadInFlightRef.current) return;
      loadInFlightRef.current = true;
      try {
        const { reels: next } = await getReels();
        setError(null);
        setReconnect(false);
        applyReels(next, opts);
      } catch (err) {
        if (isTwitchSignInRequiredError(err)) {
          if (isStudioUseMock()) {
            setMockAuthState('stale_link');
            refreshMock();
          }
          setReconnect(true);
          return;
        }
        setError('Could not load your reels. Please try again.');
      } finally {
        loadInFlightRef.current = false;
      }
    },
    [applyReels, refreshMock]
  );

  useEffect(() => {
    if (isLoggedOut || status === 'loading' || status === 'stale_link') return;
    void load({ announce: false });
  }, [isLoggedOut, status, load]);

  const hasInProgress =
    reels?.some((r) => isStudioJobInProgress(r.status)) ?? false;

  useEffect(() => {
    if (!hasInProgress || isLoggedOut || status !== 'linked') return;
    const id = window.setInterval(() => {
      void load({ announce: true });
    }, REELS_LIBRARY_POLL_MS);
    return () => window.clearInterval(id);
  }, [hasInProgress, isLoggedOut, status, load]);

  const revalidate = useCallback(() => {
    void load({ announce: false });
  }, [load]);
  useReelsLibraryRevalidate(
    revalidate,
    !isLoggedOut && status === 'linked'
  );

  const handleDelete = async (jobId: string) => {
    const snapshot = reels ?? [];
    setReels(snapshot.filter((r) => r.job_id !== jobId));
    reportReels(snapshot.filter((r) => r.job_id !== jobId));
    try {
      await deleteReel(jobId);
      prevStatusRef.current.delete(jobId);
    } catch (err) {
      setReels(snapshot);
      reportReels(snapshot);
      throw err;
    }
  };

  const handleRename = async (jobId: string, reelName: string) => {
    const snapshot = reels ?? [];
    const optimistic = snapshot.map((row) =>
      row.job_id === jobId
        ? { ...row, reel_name: reelName, needs_reel_name: false }
        : row
    );
    setReels(optimistic);
    reportReels(optimistic);
    try {
      const updated = await renameReel(jobId, reelName);
      setReels((prev) =>
        (prev ?? []).map((row) => (row.job_id === jobId ? { ...row, ...updated } : row))
      );
    } catch (err) {
      setReels(snapshot);
      reportReels(snapshot);
      throw err;
    }
  };

  if (status === 'loading') {
    return (
      <div className="flex justify-center py-16">
        <div className="studio-spinner" />
      </div>
    );
  }

  if (isLoggedOut) {
    return <Navigate to="/studio/connect" replace />;
  }

  if (status === 'stale_link' || reconnect) {
    return (
      <div data-testid="studio-reels-reconnect">
        <ReconnectTwitchCard
          onReconnect={() => {
            const shouldNavigate = beginTwitchConnect({
              returnTo: '/studio/reels',
            });
            if (shouldNavigate) {
              setMockState('linked');
              setReconnect(false);
              void load({ announce: false });
            }
          }}
        />
      </div>
    );
  }

  if (reels === null && !error) {
    return (
      <div className="flex justify-center py-16" data-testid="studio-reels-loading">
        <div className="studio-spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="studio-panel space-y-4"
        role="alert"
        data-testid="studio-reels-error"
      >
        <p>{error}</p>
        <button
          type="button"
          className="studio-btn-ghost"
          onClick={() => void load({ announce: false })}
        >
          Try again
        </button>
      </div>
    );
  }

  const list = reels ?? [];
  const counts = reelFilterCounts(list);
  const visible = list.filter((row) => reelMatchesFilter(row, filter));
  const firstRun = list.length === 0;
  const filterEmptyCopy =
    filter === 'in_progress'
      ? STUDIO_COPY.reelsEmptyInProgress
      : filter === 'delivered'
        ? STUDIO_COPY.reelsEmptyDelivered
        : filter === 'failed'
          ? STUDIO_COPY.reelsEmptyFailed
          : STUDIO_COPY.reelsEmptyHeadline;

  const chips: { id: ReelFilter; label: string; count: number }[] = [
    { id: 'all', label: STUDIO_COPY.reelsFilterAll, count: counts.all },
    {
      id: 'delivered',
      label: STUDIO_COPY.reelsFilterDelivered,
      count: counts.delivered,
    },
    {
      id: 'in_progress',
      label: STUDIO_COPY.reelsFilterInProgress,
      count: counts.in_progress,
    },
    { id: 'failed', label: STUDIO_COPY.reelsFilterFailed, count: counts.failed },
  ];

  return (
    <div className="space-y-8" data-testid="studio-reels-page">
      <div className="space-y-2">
        <div className="studio-tape-stripe rounded-sm opacity-60" aria-hidden />
        <h1 className="studio-page-heading">{STUDIO_COPY.reelsPageHeading}</h1>
      </div>

      <div className="studio-reels-filters" data-testid="studio-reels-filters">
        {chips.map((chip) => (
          <button
            key={chip.id}
            type="button"
            className={`studio-filter-chip ${filter === chip.id ? 'is-active' : ''}`}
            data-testid={`studio-reels-filter-${chip.id}`}
            aria-pressed={filter === chip.id}
            onClick={() => setFilter(chip.id)}
          >
            {chip.label}
            <span className="studio-filter-count">{chip.count}</span>
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <div className="studio-reels-empty-panel" data-testid="studio-reels-empty">
          {firstRun ? (
            <>
              <h2 className="studio-card-title">{STUDIO_COPY.reelsEmptyHeadline}</h2>
              <p className="text-[var(--studio-muted)]">{STUDIO_COPY.reelsEmptyBody}</p>
              <Link
                to="/studio/vods"
                className="studio-btn-primary no-underline inline-flex"
                data-testid="studio-reels-empty-cta"
              >
                {STUDIO_COPY.reelsEmptyCta}
              </Link>
            </>
          ) : (
            <p className="text-[var(--studio-muted)]">{filterEmptyCopy}</p>
          )}
        </div>
      ) : (
        <div className="studio-reels-grid" data-testid="studio-reels-grid">
          {visible.map((reel) => (
            <ReelCard
              key={reel.job_id}
              reel={reel}
              onDelete={handleDelete}
              onRename={handleRename}
              onToast={setToast}
            />
          ))}
        </div>
      )}

      <StudioToast message={toast} onDismiss={() => setToast(null)} />
    </div>
  );
};

export default StudioReelsPage;
